/**
 * Bunting across the street below the apothecary (lifted from lantern-water's
 * `world.js` `streetBunting`, the apothecary at its authored place): a
 * sagging rope from beneath the shop's eave to a mast with a stone foot and a
 * brass ball, and eleven folded pennants in four colours (every third one
 * swallow-tailed), which flutter in the breeze (the kit's pennant material).
 * Placed at the origin (the rope's ends are the town's points). No params.
 * The mast's collider is the scene record's.
 */
import * as THREE from 'three';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'street_bunting',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { line, cylinder, mesh, m, kit } = ctx;
    const a = new THREE.Vector3(-7.7, 7.05, -6.22);
    const b = new THREE.Vector3(1.9, 6.3, -5.15);
    const length = Math.hypot(b.x - a.x, b.z - a.z);
    const ropeAt = (t: number): THREE.Vector3 => a.clone().lerp(b, t).add(new THREE.Vector3(0, -Math.sin(Math.PI * t) * 0.52, 0));
    for (let i = 0; i < 28; i += 1) {
      const p = ropeAt(i / 28);
      const q = ropeAt((i + 1) / 28);
      line(root, [p.x, p.y, p.z], [q.x, q.y, q.z], 0.018, m.darkWood);
    }
    // The left end is fixed beneath the shop eave; the right has a visible mast.
    cylinder(root, b.x, 4.35, b.z, 0.065, 3.9, m.darkWood);
    cylinder(root, b.x, 2.65, b.z, 0.15, 0.5, m.stone);
    mesh(root, ctx.sphere, m.brass, b.x, 6.38, b.z, 0.11, 0.11, 0.11);
    const colors = ['#95565c', '#6d889f', '#d2bb8a', '#738068'];
    for (let i = 0; i < 11; i += 1) {
      const t = 0.07 + i * 0.084;
      const width = 0.51;
      const height = 0.62 + (i % 3) * 0.075;
      const geo = new THREE.PlaneGeometry(width, height, 6, 8);
      const pos = geo.getAttribute('position');
      const uv = geo.getAttribute('uv');
      const color = new THREE.Color(colors[i % 4] ?? '#95565c');
      const vertexColors: number[] = [];
      for (let j = 0; j < pos.count; j += 1) {
        const u = uv.getX(j);
        const v = 1 - uv.getY(j);
        const taper = i % 3 === 1 ? 1 : 1 - v;
        const along = (u - 0.5) * width * taper;
        const point = ropeAt(t + along / length);
        const notch = i % 3 === 1 ? Math.max(0, v - 0.7) * (1 - Math.abs(u * 2 - 1)) * 0.8 : 0;
        point.y -= height * (v - notch);
        point.z += Math.sin(u * Math.PI * 2 + i * 0.8) * 0.035 * v;
        pos.setXYZ(j, point.x, point.y, point.z);
        const fold = 0.88 + 0.12 * Math.sin(u * Math.PI * 3 + i * 0.7);
        vertexColors.push(color.r * fold, color.g * fold, color.b * fold);
      }
      geo.setAttribute('color', new THREE.Float32BufferAttribute(vertexColors, 3));
      geo.computeVertexNormals();
      mesh(root, geo, kit.pennant, 0, 0, 0);
    }
    return { root };
  },
};
